import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useMyWorkGroupQuota } from '@/hooks/use-workgroups';
import { useWorkgroupsRealtime } from '@/hooks/use-workgroups-realtime';
import { WorkGroupQuotaBadge } from '@/features/workgroups/components/work-group-quota-badge';
import { Badge } from '@/components/ui/badge';

export function WorkGroupsLayout() {
  const { workGroupId } = useParams();
  const { hasCapability } = usePermissions();
  const realtime = useWorkgroupsRealtime(workGroupId);
  const { data: quota } = useMyWorkGroupQuota();

  const navCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      'block rounded-md px-3 py-2 text-sm transition-colors',
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
    );

  const showTrashNav = !!workGroupId && hasCapability('workgroups.trash.view');

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside className="flex w-56 shrink-0 flex-col border-r border-border bg-card">
        <div className="flex flex-1 flex-col gap-1 p-3">
          <NavLink to="/workgroups" className={navCls} end>
            All work groups
          </NavLink>
          {workGroupId ? (
            <>
              <NavLink to={`/workgroups/${workGroupId}`} className={navCls} end>
                Browse
              </NavLink>
              {showTrashNav ? (
                <NavLink to={`/workgroups/${workGroupId}/trash`} className={navCls}>
                  Trash
                </NavLink>
              ) : null}
            </>
          ) : null}
        </div>
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border px-4">
          <Link to="/start" className="text-sm font-medium text-primary hover:underline">
            Back to main
          </Link>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {workGroupId && quota ? (
              <WorkGroupQuotaBadge quota={quota} compact className="max-w-xs" />
            ) : null}
            {workGroupId && realtime.enabled ? (
            <Badge
              variant={realtime.subscribed ? 'secondary' : 'outline'}
              className="font-normal"
              title={
                realtime.subscribed
                  ? 'Live updates connected'
                  : realtime.error
                    ? `Live updates unavailable (${realtime.error})`
                    : 'Connecting live updates…'
              }
            >
              Live updates {realtime.subscribed ? 'on' : 'off'}
            </Badge>
          ) : null}
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
